/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get */
// Extending From
import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
// Ember Imports
import { computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
// External Imports
import config from '@linc-technologies/linced-common/utils/config';
import DragDrop from '@uppy/drag-drop';
import Tus from '@uppy/tus';
import Uppy from '@uppy/core';
import UppyDashboard from '@uppy/dashboard';
import Webcam from '@uppy/webcam';
import XHRUpload from '@uppy/xhr-upload';
// Internal Imports
import MediaType from '@linc-technologies/linced-common/enums/media-type';
import { isMobile } from '@linc-technologies/linced-common/utils/platform';

// IMAGE_MAX_BYTES caps image uploads just under the limit the image storage
// provider enforces on its direct upload URLs (20000000 bytes). Over that, the
// provider rejects the upload once the bytes are already on the wire ("The image
// exceeded the size limit of 20000000 bytes"), so it is restricted client-side
// to fail immediately with a message the user can see.
// 19mb rather than the provider's exact figure, so the limit shown in the
// dashboard note is a whole number that matches what is actually enforced.
const IMAGE_MAX_BYTES = 19 * Math.pow(1024, 2);
// FILE_MAX_BYTES caps constrained uploaders at 64mb.
const FILE_MAX_BYTES = 64 * Math.pow(1024, 2);
// FILE_MAX_BYTES_FALLBACK caps unconstrained uploaders at 1gb.
const FILE_MAX_BYTES_FALLBACK = Math.pow(1024, 3);

// MIME_LABELS maps accepted mime types to the extension people recognise them
// by. Only types whose label can't be derived from the mime type itself need an
// entry; everything else falls through to formatFileType, so allowing a new
// type doesn't require touching this map.
const MIME_LABELS = new Map([
  ['application/msword', 'DOC'],
  ['application/vnd.ms-excel', 'XLS'],
  ['application/vnd.ms-powerpoint', 'PPT'],
  [
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'PPTX',
  ],
  [
    'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
    'PPSX',
  ],
  ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'XLSX'],
  [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'DOCX',
  ],
  ['application/vnd.visio', 'VSD'],
  ['application/x-zip-compressed', 'ZIP'],
  ['image/jpeg', 'JPG'],
]);

// formatBytes renders a byte count in whole MB or GB for display in Uppy's
// dashboard note, so the limit is visible before a file is picked.
function formatBytes(bytes) {
  if (bytes >= Math.pow(1024, 3)) {
    return `${Math.floor(bytes / Math.pow(1024, 3))} GB`;
  }
  return `${Math.floor(bytes / Math.pow(1024, 2))} MB`;
}

// WILDCARD_FORMATS names the formats people recognise for the categories that
// are accepted wholesale, e.g. `video/*`. "Any video format" is accurate but
// useless to someone holding a .mov and wondering whether it will be taken, so
// the common extensions are named and the clause stays open-ended because the
// category really does accept more than it lists.
const WILDCARD_FORMATS = new Map([
  ['audio/*', 'MP3, WAV, M4A and other audio formats'],
  ['video/*', 'MP4, MOV, AVI, WEBM and other video formats'],
]);

// formatFileType labels one non-wildcard entry of Uppy's allowedFileTypes,
// which holds a mix of mime types and bare extensions.
function formatFileType(type) {
  if (MIME_LABELS.has(type)) {
    return MIME_LABELS.get(type);
  }

  // A bare extension, e.g. '.csv'.
  if (type.startsWith('.')) {
    return type.slice(1).toUpperCase();
  }

  // Take the most specific segment, so 'text/csv' reads as CSV.
  return type.split('/').pop().split('.').pop().toUpperCase();
}

// formatFileTypes renders accepted types as 'JPG, PNG or WEBP'. Types are
// deduplicated by label, because the lists routinely pair a mime type with the
// matching extension ('application/zip' and '.zip') to satisfy stubborn file
// pickers, and both should read as one entry.
function formatFileTypes(types) {
  let labels = new Set();
  let wildcards = [];

  types.forEach((type) => {
    if (type.endsWith('/*')) {
      wildcards.push(
        WILDCARD_FORMATS.get(type) ?? `any ${type.split('/')[0]} format`,
      );
      return;
    }
    labels.add(formatFileType(type));
  });

  let parts = [];
  if (labels.size) {
    let named = [...labels];
    parts.push(
      named.length < 2
        ? named[0]
        : `${named.slice(0, -1).join(', ')} or ${named[named.length - 1]}`,
    );
  }
  // Wildcard clauses carry their own conjunction, so they trail the named list
  // rather than being folded into it, where a second 'or' would read badly.
  parts.push(...wildcards);

  return parts.join(', plus ');
}

export default class UppyService extends Service {
  // Services
  @service
  fetch;

  @service
  session;

  @service
  store;

  @service
  toaster;

  @tracked
  imageUppy = null;
  @tracked
  videoUppy = null;
  @tracked
  fileUppy = null;

  // allowedImageTypes lists the image formats accepted wherever an image is
  // uploaded as content in its own right: avatars, banners, badges, logos. Kept
  // to formats that are still, universally supported and safe to hand to the
  // image storage provider.
  @computed
  get allowedImageTypes() {
    return ['image/jpeg', 'image/png', 'image/webp'];
  }

  // allowedAttachmentImageTypes widens that list for post attachments, where an
  // image is a document a user is sharing rather than site furniture. GIF is
  // wanted there and not on an avatar, where an animated profile picture isn't
  // the intent.
  @computed('allowedImageTypes')
  get allowedAttachmentImageTypes() {
    return ['image/bmp', 'image/gif', ...this.allowedImageTypes];
  }

  @computed
  get allowedVideoTypes() {
    return ['video/*'];
  }

  @computed
  get allowedFileTypes() {
    return [
      'audio/*',
      'application/pdf',
      'application/zip',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.visio',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
  }

  @computed
  get webcamVideoModes() {
    return ['video-audio', 'video-only', 'audio-only'];
  }

  setupAttachments(onUploadSuccess) {
    // Maintain two uppy instances (one for images and one for files)
    // This has the downside of breaking if you have multiple components
    // accessing this service at the same time... Which we don't. Yet.
    if (isEmpty(this.get('fileUppy'))) {
      let allowedTypes = this.allowedFileTypes;
      let fullWebcamEnabled = false;
      this.fileUppy = this.initAttachmentUppy(
        'file',
        6,
        allowedTypes,
        'Add Files',
        onUploadSuccess,
        fullWebcamEnabled,
      );
    } else {
      let fileUppy = this.get('fileUppy');
      fileUppy.off('upload-success');
      fileUppy.on('upload-success', (file, upload) => {
        onUploadSuccess(file, upload, MediaType.UNSPECIFIED);
      });
    }
    if (isEmpty(this.get('imageUppy'))) {
      this.imageUppy = this.initAttachmentUppy(
        'image',
        6,
        this.allowedAttachmentImageTypes,
        'Add Images',
        onUploadSuccess,
        false,
      );
    } else {
      let imageUppy = this.get('imageUppy');
      imageUppy.off('upload-success');
      imageUppy.on('upload-success', (file, upload) => {
        onUploadSuccess(file, upload, MediaType.IMAGE);
      });
    }
    if (isEmpty(this.get('videoUppy'))) {
      this.videoUppy = this.initAttachmentUppy(
        'video',
        6,
        this.allowedVideoTypes,
        'Add Videos',
        onUploadSuccess,
        false,
      );
    } else {
      let videoUppy = this.get('videoUppy');
      videoUppy.off('upload-success');
      videoUppy.on('upload-success', (file, upload) => {
        onUploadSuccess(file, upload, MediaType.VIDEO);
      });
    }
  }

  addImages() {
    this.openAttachmentDashboard(this.imageUppy);
  }

  addVideos() {
    this.openAttachmentDashboard(this.videoUppy);
  }

  addFiles() {
    this.openAttachmentDashboard(this.fileUppy);
  }

  // openAttachmentDashboard opens the picker, dropping any files left behind by
  // a previous failure. Failed files are kept on `complete` so their error stays
  // visible, so they have to be cleared on the way back in. Only errored files
  // are removed, because the modal can be closed while an upload is still
  // running and that upload must survive being reopened.
  openAttachmentDashboard(uppy) {
    uppy.getFiles().forEach((file) => {
      if (file.error) {
        uppy.removeFile(file.id);
      }
    });
    uppy.getPlugin('Dashboard').openModal();
  }

  async uploadScreenshot(file) {
    this.imageUppy.setOptions({ autoProceed: false });
    this.imageUppy.addFile({
      name: file.name,
      type: file.type,
      data: file,
    });
    try {
      await this.imageUppy.upload();
    } catch (error) {
      this.toaster.error(`Upload failed: ${error}`);
    }

    this.imageUppy.setOptions({ autoProceed: true });
  }

  initAttachmentUppy(
    mode,
    numFiles,
    allowedTypes,
    note,
    onUploadSuccess,
    fullWebcamEnabled,
  ) {
    let uppy = this.constructUppy(null, numFiles, allowedTypes, true, mode);
    if (mode !== 'file') {
      this.configureWebcam(uppy, mode, fullWebcamEnabled);
    }
    this.configureDashboard(uppy, this.noteWithRestrictions(uppy, note), null);
    let mediaType = MediaType.UNSPECIFIED;
    this.configureFileUpload(uppy, mode);
    switch (mode) {
      case 'file':
        mediaType = MediaType.UNSPECIFIED;
        break;
      case 'image':
        mediaType = MediaType.IMAGE;
        break;
      case 'video':
        mediaType = MediaType.VIDEO;
        break;
    }

    this.configureUppyEventHandlers(
      uppy,
      null,
      null,
      (file, upload) => onUploadSuccess(file, upload, mediaType),
      () => uppy.getPlugin('Dashboard').closeModal(),
      (file, error) => this.reportUploadError(file, error),
    );

    return uppy;
  }

  initFileUploaderUppy(
    mode,
    config,
    allowedTypes,
    targetElementId,
    dropPrompt,
    clickPrompt,
    constrainSize,
    onDashboardClose,
    onUploadStart,
    onUploadSuccess,
    onError,
  ) {
    let uppy = this.constructUppy(config, 1, allowedTypes, constrainSize, mode);
    this.configureDragDrop(uppy, targetElementId, dropPrompt, clickPrompt);
    this.configureWebcam(uppy, mode);
    this.configureDashboard(
      uppy,
      this.noteWithRestrictions(uppy, ''),
      onDashboardClose,
    );
    this.configureFileUpload(uppy, mode);

    this.configureUppyEventHandlers(
      uppy,
      null,
      onUploadStart,
      onUploadSuccess,
      null,
      onError,
    );

    return uppy;
  }

  initFilePickerUppy(
    config,
    allowedTypes,
    targetElementId,
    dropPrompt,
    clickPrompt,
    onFileAdded,
  ) {
    let uppy = this.constructUppy(config, 1, allowedTypes, true);
    this.configureDragDrop(uppy, targetElementId, dropPrompt, clickPrompt);

    this.configureUppyEventHandlers(uppy, onFileAdded, null, null, null, null);

    return uppy;
  }

  constructUppy(fullConfig, numFiles, allowedTypes, constrainSize, mode) {
    if (fullConfig) {
      return new Uppy(fullConfig);
    }

    let restrictions = {
      maxNumberOfFiles: numFiles,
      // `null` means unrestricted. An empty list is normalised to it, because
      // Uppy guards with `if (allowedFileTypes)` and an empty array is truthy,
      // so `[].some(...)` fails every file and nothing can be picked at all.
      allowedFileTypes: isEmpty(allowedTypes) ? null : allowedTypes.slice(),
      maxFileSize: constrainSize ? FILE_MAX_BYTES : FILE_MAX_BYTES_FALLBACK,
    };

    if (mode === 'image') {
      // Images have a tighter ceiling than files regardless of the widget's
      // size, because the storage provider rejects anything larger.
      restrictions.maxFileSize = Math.min(
        restrictions.maxFileSize,
        IMAGE_MAX_BYTES,
      );
    }

    return new Uppy({
      debug: false,
      autoProceed: true,
      restrictions,
    });
  }

  configureDragDrop(uppy, elementId, dropPrompt, clickPrompt) {
    uppy.use(DragDrop, {
      target: '#' + elementId,
      locale: {
        strings: {
          dropHereOr: dropPrompt,
          browse: clickPrompt,
        },
      },
    });
  }

  configureWebcam(uppy, mode, fullWebcamEnabled) {
    if (!isMobile()) {
      let modes = this.webcamVideoModes.slice();
      if (fullWebcamEnabled) {
        // Enable all webcam modes (static + video).
        modes.unshift('picture');
      } else if (mode === 'image') {
        // This is an image-only instance; limit it to static images.
        modes = ['picture'];
      }
      uppy.use(Webcam, {
        modes,
        // Uppy 4 moved `facingMode` under `videoConstraints`; passing it at the
        // top level is silently ignored.
        videoConstraints: { facingMode: 'environment' },
        mirror: false,
        showVideoSourceDropdown: true,
      });
    }
  }

  // noteWithRestrictions appends the instance's accepted formats and max file
  // size to the dashboard note, so both are stated up front rather than only on
  // rejection. Derived from the restrictions actually in force, so the note
  // can't drift from what the uploader accepts.
  noteWithRestrictions(uppy, note) {
    let { allowedFileTypes, maxFileSize } = uppy.opts?.restrictions ?? {};

    // Formats and size limit go on separate lines so neither gets lost in a
    // run-on sentence. Uppy renders the note as one text node, so the breaks
    // rely on `white-space: pre-line` from linced-common-overrides.scss.
    let lines = [];
    if (!isEmpty(allowedFileTypes)) {
      let formats = formatFileTypes(allowedFileTypes);
      lines.push(
        isEmpty(note)
          ? formats.charAt(0).toUpperCase() + formats.slice(1)
          : `${note}: ${formats}`,
      );
    } else if (!isEmpty(note)) {
      lines.push(note);
    }

    if (maxFileSize) {
      lines.push(`Up to ${formatBytes(maxFileSize)} each`);
    }

    return lines.join('\n');
  }

  // reportUploadError surfaces a failed upload as a toast. Without it a
  // rejection from the storage provider only reaches the console, and the
  // dashboard closes as though nothing happened.
  reportUploadError(file, error) {
    let name = file?.name ? `${file.name}: ` : '';
    this.toaster.error(
      `${name}${error?.message || 'upload failed. Please try again.'}`,
    );
  }

  configureDashboard(uppy, note, onDashboardClose) {
    uppy.use(UppyDashboard, {
      showLinkToFileUploadResult: false,
      hideUploadButton: true,
      hideProgressAfterFinish: true,
      // Drops the status bar's secondary line, which carried a time remaining
      // estimate that was wildly inaccurate: it extrapolates from throughput so
      // far, which swings hard on a chunked upload. Uppy has no ETA-only
      // switch, and the estimate is a bare text node between hardcoded
      // separators, so it can't be targeted on its own. The percentage on the
      // primary line and the per-file progress bars are unaffected.
      hideProgressDetails: true,
      plugins: ['Webcam'],
      note,
      onRequestCloseModal: () => {
        if (onDashboardClose) {
          onDashboardClose();
        }
        uppy.getPlugin('Dashboard').closeModal();
      },
    });
  }

  configureFileUpload(uppy, mode) {
    switch (mode) {
      case 'image':
        uppy
          .use(XHRUpload, {
            fieldName: 'file',
            formData: true,
            allowedMetaFields: [],
          })
          .addPreProcessor((fileIDs) =>
            this.getUploadURL(uppy, fileIDs, MediaType.IMAGE),
          );
        break;
      case 'video':
        uppy
          .use(XHRUpload, {
            fieldName: 'file',
            formData: true,
            allowedMetaFields: [],
            // Cloudflare Stream returns an empty body, which is not JSON. The
            // video id comes from file.cfUid, so the body is not necessary.
            getResponseData: () => ({}),
          })
          .addPreProcessor((fileIDs) =>
            this.getUploadURL(uppy, fileIDs, MediaType.VIDEO),
          );
        break;
      default:
        uppy
          .use(Tus, {
            endpoint: config.APP.endpoints.tus,
            // chunkSize is set to 5MB chunks - S3's minimum
            chunkSize: 1024 * 1024 * 5,
          })
          .addPreProcessor((fileIDs) => this.setTusHeaders(uppy, fileIDs));
    }
  }

  async getUploadURL(uppy, fileIDs, mediaType) {
    // Create an array of promises for each file upload URL request
    const uploadPromises = fileIDs.map(async (fileID) => {
      const payload = {
        mediaType,
      };
      const response = await this.fetch.post(
        `${config.APP.apiHost}/media/${config.APP.apiPath}/file/:draft`,
        {
          data: payload,
        },
      );

      // Set the file state with the received upload URL
      uppy.setFileState(fileID, {
        xhrUpload: { endpoint: response.uploadUrl },
        cfUid: response.uid,
      });
    });

    // Wait for all the upload URLs to be set
    await Promise.all(uploadPromises);
  }

  async setTusHeaders(uppy, fileIDs) {
    // Fetch the latest token dynamically
    const tokenWrapper = await this.session.getToken();

    // Attach the dynamic headers to each file's metadata
    fileIDs.forEach((fileID) => {
      const file = uppy.getFile(fileID);

      // Add headers to metadata (accessible by Tus plugin)
      uppy.setFileState(fileID, {
        tus: {
          ...file.tus,
          headers: {
            Authorization: `Bearer ${tokenWrapper.token}`,
            'x-tenant-id': this.session.activeSchool,
          },
        },
      });
    });
  }

  configureUppyEventHandlers(
    uppy,
    onFileAdded,
    onUploadStart,
    onUploadSuccess,
    onComplete,
    onError,
  ) {
    uppy.off('file-added');
    uppy.on('file-added', (file) => this.sanitiseFileNames(uppy, file));
    if (onFileAdded) {
      uppy.on('file-added', (file) => onFileAdded(file));
    }

    uppy.off('upload');
    if (onUploadStart) {
      uppy.on('upload', () => onUploadStart());
    }

    uppy.off('upload-success');
    if (onUploadSuccess) {
      uppy.on('upload-success', (file, response) =>
        onUploadSuccess(file, response),
      );
    }

    uppy.off('upload-error');
    if (onError) {
      uppy.on('upload-error', (file, error, response) =>
        onError(file, error, response),
      );
    }

    uppy.off('complete');
    uppy.on('complete', (result) => {
      // Leave failed files in place. `cancelAll` clears Uppy's own per-file
      // error UI, and `onComplete` closes the dashboard, so running either
      // after a failure dismisses the only evidence the upload went wrong.
      if (result?.failed?.length) {
        return;
      }

      uppy.cancelAll();
      if (onComplete) {
        onComplete();
      }
    });

    uppy.off('restriction-failed');
    uppy.on('restriction-failed', (file, error) => {
      // `error` is an Error; the toaster renders whatever it is given, so pass
      // the message rather than the object.
      this.toaster.error(error?.message || 'That file cannot be uploaded.');
    });
  }

  sanitiseFileNames(uppy, file) {
    let sanitizedFilename = file.name
      .replace(/[^a-zA-Z0-9.]/g, '_') // Replace non-alphanumeric, non-period characters with a dash
      .toLowerCase();

    if (isEmpty(sanitizedFilename)) {
      sanitizedFilename = 'image';
    }

    // Update the filename with the sanitized version
    uppy.setFileMeta(file.id, { name: sanitizedFilename });
  }
}
